import { z } from "zod"

import type { DemandScope } from "~/utils/demandProjection"
import { WHOLE_VARIANT_SCOPE } from "~/utils/demandScope"

export type StrategyFilters = Record<string, string[]>

export const StrategyFiltersSchema = z.record(
  z.string().max(200),
  z.array(z.string().max(200)).min(1).max(500),
)

const CUBE_AXES = ["warehouseId", "channel", "marketId"] as const

type CubeAxis = (typeof CUBE_AXES)[number]

export const isCubeAxis = (level: string): level is CubeAxis =>
  (CUBE_AXES as readonly string[]).includes(level)

export const AdjustmentFiltersSchema = StrategyFiltersSchema.refine(
  (filters) => Object.keys(filters).every(isCubeAxis),
  "An adjustment can only be scoped to channels, markets and warehouses.",
)

export const readStrategyFilters = (raw: unknown): StrategyFilters => {
  const parsed = StrategyFiltersSchema.safeParse(raw)
  return parsed.success ? parsed.data : {}
}

export const hasCubeFilters = (filters: StrategyFilters): boolean =>
  CUBE_AXES.some((axis) => (filters[axis]?.length ?? 0) > 0)

export const strategyCells = (filters: StrategyFilters): DemandScope[] => {
  let cells: DemandScope[] = [WHOLE_VARIANT_SCOPE]
  for (const axis of CUBE_AXES) {
    const values = filters[axis]
    if (!values?.length) continue
    cells = cells.flatMap((cell) => values.map((value) => ({ ...cell, [axis]: value })))
  }
  return cells
}

export const scopeToFilters = (scope: DemandScope): StrategyFilters =>
  Object.fromEntries(
    CUBE_AXES.flatMap((axis) => {
      const value = scope[axis]
      return value == null ? [] : [[axis, [value]]]
    }),
  )

export const withoutLevel = (filters: StrategyFilters, level?: string): StrategyFilters => {
  if (level == null || !(level in filters)) return filters
  const { [level]: _dropped, ...rest } = filters
  return rest
}

export const onlyLevels = (filters: StrategyFilters, levels: readonly string[]): StrategyFilters =>
  Object.fromEntries(Object.entries(filters).filter(([level]) => levels.includes(level)))
